import { Loader } from '@mantine/core';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { MoneyTransferForm } from 'src/Components/Forms/MoneyTransfer/MoneyTransferForm.component';
import { useUpdateMoneyTransfer } from 'src/Hooks/Mutations/useUpdateMoneyTransfer';
import useFetchMoneyTransfers from 'src/Hooks/Queries/useFetchMoneyTransfers';
import { trackLinkedReimbursementEdited } from 'src/Services/Mixpanel/events';
import { ApiError } from 'src/types/api-error.types';

/**
 * Mêmes refus que la création : leurs messages nomment la dépense ou le montant attendu,
 * qu'aucun libellé générique ne remplace.
 */
const REIMBURSEMENT_ERROR_CODES = [
  'REIMBURSEMENT_AMOUNT_MISMATCH',
  'REIMBURSEMENT_MIXED_CASE',
  'REIMBURSEMENT_BY_PARTICIPANT_UNSUPPORTED',
  'REIMBURSEMENT_EXPENSE_NOT_ELIGIBLE',
];

/** Reconstruit la valeur de Select d'une partie, avec le préfixe attendu par le formulaire. */
const partyValue = (party?: {
  id: string;
  type: 'USER' | 'BANK_ACCOUNT' | 'PLACEHOLDER_MEMBER';
}): string | null => {
  if (!party) return null;

  switch (party.type) {
    case 'BANK_ACCOUNT':
      return `bank-${party.id}`;
    case 'PLACEHOLDER_MEMBER':
      return `placeholder-${party.id}`;
    default:
      return `user-${party.id}`;
  }
};

const EditMoneyTransferDrawer = () => {
  const { t } = useTranslation();
  const { transferId } = useParams<{ transferId: string }>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutate, isLoading } = useUpdateMoneyTransfer();

  // La liste est déjà en cache : on y retrouve le transfert plutôt que d'ouvrir un endpoint
  // de détail que le back n'expose pas.
  const { data: transfers, isLoading: transfersLoading } = useFetchMoneyTransfers();
  const transfer = transfers?.find(item => item.id === transferId);

  const getErrorMessage = (error: unknown): string => {
    const data = (error as AxiosError<ApiError>)?.response?.data;
    if (data?.code && REIMBURSEMENT_ERROR_CODES.includes(data.code)) {
      return data.message ?? t(`apiErrors.${data.code}`);
    }
    return t('common.genericError', 'Une erreur est survenue. Merci de réessayer.');
  };

  return (
    <AnimatedRoutedDrawer title={t('moneyTransfer.edit.title', 'Modifier le remboursement')}>
      {({ onClose }) => {
        if (transfersLoading) return <Loader />;
        if (!transfer)
          return <div>{t('moneyTransfer.edit.notFound', 'Remboursement introuvable')}</div>;

        return (
          <MoneyTransferForm
            lockParties
            submitLabel={t('common.save', 'Enregistrer')}
            initialValues={{
              amount: transfer.amount,
              date: new Date(transfer.date),
              source: partyValue(transfer.from),
              destination: partyValue(transfer.to),
              expenseIds: (transfer.expenses ?? []).map(expense => expense.id),
            }}
            onSubmit={values => {
              setSubmitError(null);
              mutate(
                {
                  id: transfer.id,
                  data: {
                    amount: values.amount,
                    date: values.date,
                    // Toujours transmis, y compris vide : c'est ce qui permet de décocher
                    // la dernière dépense et de redevenir un transfert libre.
                    expenseIds: values.expenseIds ?? [],
                  },
                },
                {
                  onSuccess: () => {
                    trackLinkedReimbursementEdited({
                      expenses_count: values.expenseIds?.length ?? 0,
                      total_amount: values.amount,
                    });
                    onClose();
                  },
                  onError: error => setSubmitError(getErrorMessage(error)),
                }
              );
            }}
            onCancel={onClose}
            isLoading={isLoading}
            submitError={submitError}
          />
        );
      }}
    </AnimatedRoutedDrawer>
  );
};

export default EditMoneyTransferDrawer;
